import React, { useEffect, useLayoutEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Navbar from './components/Navbar';
import TemplateList from './components/TemplateList';
import TemplateForm from './components/TemplateForm';
import InterviewForm from './components/InterviewForm';
import InterviewList from './components/InterviewList';
import Landing from './components/Landing';
import DemoThankYou from './components/DemoThankoyou'
import Solutions from './components/Solutions';
import HighVolumeHiringPage from './components/SolutionByIndustry/HighVolumeHiringPage';
import DeveloperHiringPage from './components/SolutionByIndustry/DeveloperHiringPage';
import TechnicalScreeningPage from './components/SolutionByIndustry/TechnicalScreeningPage';
import RemoteHiringPage from './components/SolutionByIndustry/RemoteHiringPage';
import CampusHiringPage from './components/SolutionByIndustry/CampusHiringPage';
import BfsiPage from './components/SolutiuonByUseCase/BfsiPage';
import HealthcarePage from './components/SolutiuonByUseCase/HealthcarePage';
import ItServicesSaasPage from './components/SolutiuonByUseCase/ItServicesSaasPage';
import EcommercePage from './components/SolutiuonByUseCase/EcommercePage';
import ManufacturingPage from './components/SolutiuonByUseCase/ManufacturingPage';
import Features from './components/Features';
import FaqPage from './components/FaqPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfServices from './components/TermsOfServices';
import PublicTemplatePreview from './components/PublicTemplatePreview';
import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import SSOCallback from './components/Auth/SSOCallback';
import Dashboard from './components/Dashboard';
import DashboardLayout from './components/DashboardLayout';
import RouteGuard from './components/AccessControl/RouteGuard';
import { isAuthenticated, clearSession, fetchUserById } from './services/auth';
import Contact from './components/Contact';
import Footer from '../interviewAI/layout/Footer';
import './App.css';
import { NotificationProvider } from '../interviewAI/NotificationContext';
import { RBACProvider } from './context/RBACContext';
import UserManagement from './components/UserManagement';
import Settings from './components/Settings';
import ProctoringAnalysis from './components/ProctoringAnalysis';
import InterviewVideoViewer from './components/InterviewVideoViewer';
import ContactModal from './components/ContactModal';
import { closeDemoModal } from './utils/demoModal';
import { trackCtaClick, trackModalOpen, trackPageVisit } from './services/webTracking';
import { useTheme } from '../theme/ThemeContext';

const createCustomTheme = (colors, currentTheme) => createTheme({
  palette: {
    mode: currentTheme === 'dark' ? 'dark' : 'light',
    primary: {
      main: colors.primary,
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.bgCard,
      contrastText: colors.primary,
    },
    background: {
      default: colors.bgPage,
      paper: colors.bgCard,
    },
    text: {
      primary: colors.textPrimary,
      secondary: colors.textSecondary,
    },
    divider: colors.borderColor,
  },
  typography: {
    fontFamily: [
      'Inter', 'Poppins', 'Nunito Sans',
      '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'Helvetica Neue', 'Arial', 'Noto Sans', 'sans-serif'
    ].join(','),
    h1: { fontWeight: 800 },
    h2: { fontWeight: 800 },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 800 },
    body1: { fontSize: '1rem' },
    body2: { fontSize: '0.95rem' }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: colors.bgMain,
          color: colors.textPrimary,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: colors.bgCard,
          backgroundImage: 'none',
          color: colors.textPrimary,
          borderColor: colors.borderColor,
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: colors.headerBg,
          boxShadow: colors.shadow,
          color: colors.textPrimary,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          fontWeight: 700,
        },
        containedPrimary: {
          backgroundColor: colors.primary,
          color: '#ffffff',
          '&:hover': { backgroundColor: colors.primaryHover },
        },
        outlinedPrimary: {
          borderColor: colors.primary,
          color: colors.primary,
          '&:hover': { borderColor: colors.primaryHover, backgroundColor: colors.primaryLight },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          color: colors.textSecondary,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: colors.bgInput,
          borderRadius: 12,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.borderColor,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary,
          },
          color: colors.textPrimary,
        },
        input: {
          fontSize: '0.95rem',
        },
      }
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.95rem',
          color: colors.textPrimary,
          borderColor: colors.borderColor,
        },
        head: {
          fontWeight: 800,
          color: colors.textSecondary,
          backgroundColor: colors.bgPage,
        },
      },
    },
  },
});


import { useLocation } from 'react-router-dom';

function App() {
  const { themeColors, currentTheme } = useTheme();
  const theme = React.useMemo(() => createCustomTheme(themeColors, currentTheme), [themeColors, currentTheme]);
  
  const location = useLocation();
  const [isDemoModalOpen, setIsDemoModalOpen] = React.useState(false);

  useEffect(() => {
    const handleOpenDemoModal = () => {
      trackModalOpen();
      setIsDemoModalOpen(true);
    };
    const handleCloseDemoModal = () => setIsDemoModalOpen(false);
    window.addEventListener('tt:open-contact-modal', handleOpenDemoModal);
    window.addEventListener('tt:close-contact-modal', handleCloseDemoModal);
    return () => {
      window.removeEventListener('tt:open-contact-modal', handleOpenDemoModal);
      window.removeEventListener('tt:close-contact-modal', handleCloseDemoModal);
    };
  }, []);

  useEffect(() => {
    trackPageVisit();
  }, [location.pathname, location.search]);

  useEffect(() => {
    const trackClick = (event) => {
      if (!(event.target instanceof Element)) return;
      const element = event.target.closest("a, button, [role='button']");
      if (!element || element.disabled || element.getAttribute("aria-disabled") === "true") return;
      if (element.type === "submit") return;
      const className = typeof element.className === "string" ? element.className : "";
      const isCta = element.dataset.trackCta || /(^|\s)(btn|nbtn|tt-exit-submit)(\s|$)/.test(className);
      if (!isCta) return;
      const label = element.dataset.trackCta || element.getAttribute("aria-label") || element.textContent;
      trackCtaClick(label);
    };
    document.addEventListener("click", trackClick, true);
    return () => document.removeEventListener("click", trackClick, true);
  }, []);

  const handleCloseDemoModal = () => {
    closeDemoModal();
  };

  // Route changes start at the top unless the URL explicitly names a section.
  useLayoutEffect(() => {
    const hash = location.hash;
    if (!hash || hash === '#') {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      return undefined;
    }

    let frame = 0;
    frame = window.requestAnimationFrame(() => {
      let targetId = hash.slice(1);
      try {
        targetId = decodeURIComponent(targetId);
      } catch {
        // Keep the raw fragment if it is not valid URI encoding.
      }
      const target = document.getElementById(targetId);
      if (target) {
        target.scrollIntoView({ behavior: 'instant', block: 'start' });
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [location.key, location.pathname, location.search, location.hash]);

  useLayoutEffect(() => {
    const previousRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = previousRestoration;
    };
  }, []);

  useEffect(() => {
    const validateSession = async () => {
      if (!isAuthenticated()) return;
  
      const user = await fetchUserById();
  
      if (!user) {
        clearSession();
        window.location.reload();
      }
    };
  
    validateSession();
  }, []);
  const hideNavbar = location.pathname.startsWith('/dashboard') || location.pathname.startsWith('/templates') || location.pathname.startsWith('/interviews') || location.pathname.startsWith('/user-management') || location.pathname.startsWith('/settings') || location.pathname.startsWith('/proctoring-analysis') || location.pathname.startsWith('/interview-video') || location.pathname.startsWith('/login') || location.pathname.startsWith('/signup') || location.pathname.startsWith('/reset-password') || location.pathname.startsWith('/auth/callback') || location.pathname.startsWith('/interview/job_template_link/');
  const hideFooter = location.pathname === '/privacy-policy' || location.pathname === '/terms-of-service' || location.pathname.startsWith('/dashboard') || location.pathname.startsWith('/templates') || location.pathname.startsWith('/interviews') || location.pathname.startsWith('/user-management') || location.pathname.startsWith('/settings') || location.pathname.startsWith('/proctoring-analysis') || location.pathname.startsWith('/interview-video') || location.pathname.startsWith('/login') || location.pathname.startsWith('/signup') || location.pathname.startsWith('/reset-password') || location.pathname.startsWith('/forgot-password') || location.pathname.startsWith('/auth/callback') || location.pathname.startsWith('/interview/job_template_link/');

  // Note: Token expiration is handled by the server (HttpOnly cookies)
  // When a cookie expires, the server returns 401 Unauthorized
  // This triggers a session clear and redirect in API interceptors
  // No client-side expiration check needed

  return (
    <NotificationProvider>
      <RBACProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <div className="App">
            {!hideNavbar && <Navbar />}
            <Routes>
              {/* Public */}
              <Route path="/" element={<Landing />} />
              <Route path="/demo-thankyou" element={<DemoThankYou />} />
              <Route path="/features" element={<Features/>} />
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/solutions/high-volume-hiring" element={<HighVolumeHiringPage />} />
              <Route path="/solutions/developer-hiring" element={<DeveloperHiringPage />} />
              <Route path="/solutions/technical-screening" element={<TechnicalScreeningPage />} />
              <Route path="/solutions/remote-hiring" element={<RemoteHiringPage />} />
              <Route path="/solutions/campus-hiring" element={<CampusHiringPage />} />
              <Route path="/solutions/bfsi" element={<BfsiPage />} />
              <Route path="/solutions/healthcare" element={<HealthcarePage />} />
              <Route path="/solutions/it-services-saas" element={<ItServicesSaasPage />} />
              <Route path="/solutions/ecommerce" element={<EcommercePage />} />
              <Route path="/solutions/manufacturing" element={<ManufacturingPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/about" element={<Contact />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfServices />} />
              <Route path="/interview/job_template_link/:templateId" element={<PublicTemplatePreview />} />
              <Route path="/login" element={isAuthenticated() ? <Navigate to="/dashboard" replace /> : <Login />} />
              <Route path="/signup" element={isAuthenticated() ? <Navigate to="/dashboard" replace /> : <Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/auth/callback" element={<SSOCallback />} />

              {/* Private - with RBAC */}
              <Route path="/dashboard" element={<RouteGuard requiredResources={['Overview']} requiredAction="Get Data"><Dashboard /></RouteGuard>} />
              <Route path="/templates" element={<RouteGuard requiredResources={['View Job Openings']} requiredAction="Get Data"><DashboardLayout><TemplateList /></DashboardLayout></RouteGuard>} />
              <Route path="/templates/new" element={<RouteGuard requiredResources={['Create Job Opening']} requiredAction="Add New"><DashboardLayout><TemplateForm /></DashboardLayout></RouteGuard>} />
              <Route path="/templates/edit/:id" element={<RouteGuard requiredResources={['View Job Openings']} requiredAction="Edit Item"><DashboardLayout><TemplateForm /></DashboardLayout></RouteGuard>} />
              <Route path="/templates/clone/:id" element={<RouteGuard requiredResources={['Create Job Opening']} requiredAction="Add New"><DashboardLayout><TemplateForm /></DashboardLayout></RouteGuard>} />
              <Route path="/interviews" element={<RouteGuard requiredResources={['View Interviews ']} requiredAction="Get Data"><DashboardLayout><InterviewList /></DashboardLayout></RouteGuard>} />
              <Route path="/interviews/new" element={<RouteGuard requiredResources={['Schedule Interview']} requiredAction="Add New"><DashboardLayout><InterviewForm /></DashboardLayout></RouteGuard>} />
              <Route path="/interviews/edit/:id" element={<RouteGuard requiredResources={['View Interviews ']} requiredAction="Edit Item"><DashboardLayout><InterviewForm /></DashboardLayout></RouteGuard>} />
              <Route path="/user-management" element={<RouteGuard requiredResources={['User Management']} requiredAction="Get Data"><DashboardLayout><UserManagement /></DashboardLayout></RouteGuard>} />
              <Route path="/settings" element={<RouteGuard requiredResources={['Settings']} requiredAction="Get Data"><DashboardLayout><Settings /></DashboardLayout></RouteGuard>} />
              <Route path="/proctoring-analysis" element={<RouteGuard requiredResources={['View Interviews ']} requiredAction="Get Data"><DashboardLayout><ProctoringAnalysis /></DashboardLayout></RouteGuard>} />
              <Route path="/interview-video/:interviewId" element={<RouteGuard requiredResources={['View Interviews ']} requiredAction="Get Data"><InterviewVideoViewer /></RouteGuard>} />
            </Routes>
            <ContactModal isOpen={isDemoModalOpen} onClose={handleCloseDemoModal} />
            {!hideFooter && <Footer />}
          </div>
        </ThemeProvider>
      </RBACProvider>
    </NotificationProvider>
  );
}

export default App;
